import React, { useContext, useEffect, useState } from "react";
import {
  Table,
  Space,
  Button,
  Input,
  Drawer,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { PageHeader } from "../../../../components/page-header/page-header";
import useFetch from "../../../../hooks/useFetch";
import {
  AddEditFaultCategory,
  GetFaultCategoryList,
} from "../../../../utils/api.constant";
import { ePrivileges, eResultCode } from "../../../../utils/enum";
import useToast from "../../../../hooks/useToast";
import { ToastType, ToastOpen } from "../../../../state/toast/slice";
import CustomDrawer from "../../../../components/drawer";
import FaultCategoryForm from "./form";
import { UserContext } from "../../../../context";
import ActionCard from "../../../../components/actionCard/actionCard";
import { TContext, TFilterModel, TPrivilege } from "../../../../types/config";
import GenericPopover from "../../../../components/pop-over";
import {
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  CloseOutlined,
  DeleteOutlined,
  EditFilled,
  FilterOutlined,
  IdcardFilled,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import utils from "../../../../utils";
import ColumnManager from "../../../../components/configurable-columns";
import ExportToCSV from "../../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../../utils/date";
import { defaultFilterParams } from "../../../../utils/constants";
import {
  saveColumnPreferences,
  loadColumnPreferences,
  clearColumnPreferences,
} from "../../../../utils/column-management";
import { TFaultCategory, TOverallList } from "../../../../types";
import CustomModal from "../../../../components/modal";
import Utils from "../../../../utils";
import { filter } from "lodash";
import { useColumnSearch } from "../../../../components/table/column-search";

const FAULT_LIST_COLUMN_KEY = "faultCategoryList";

export default function FaultCategoryList() {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TFaultCategory[]>([]);
  const [faultCategory, setFaultCategory] = useState<TFaultCategory | null>();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const context: TContext = useContext(UserContext);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const { getColumnSearchProps, getDateRangeFilterProps } =
    useColumnSearch<TFaultCategory>();

  const handleDelete = (faultCategory: TFaultCategory) => {
    setFaultCategory(faultCategory);
    setShowDeleteModal(true);
  };

  const initialColumns: any[] = [
    {
      title: "Name",
      dataIndex: "problemName",
      key: "problemName",
      // width: 450,
      filterSearch: true,
      onFilter: (value: string | number | boolean, record: TFaultCategory) =>
        record.problemName
          ?.toLowerCase()
          ?.includes((value as string).toLowerCase()),
      sorter: (a: TFaultCategory, b: TFaultCategory) =>
        a.problemName?.localeCompare?.(b.problemName),
      filteredValue: activeFilters.problemName || null,
    },
    {
      title: "Added On",
      dataIndex: "createdOn",
      key: "createdOn",
      // width: 150,
      sorter: (a: TFaultCategory, b: TFaultCategory) =>
        new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
      ...getDateRangeFilterProps("createdOn"),
      render: (createdOn: string) => {
        if (createdOn) {
          return DateUtils.format(createdOn, context?.user?.dateFormat, true);
        }
        return "-"; // Or any placeholder for empty values
      },
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      fixed: "right",
      width: 80,
      render: (record: TFaultCategory) => (
        <GenericPopover
          placement="left"
          content={
            <div className="standardActionBox">
              {Utils.isUserHasAccess(
                context.user?.privilegeList as TPrivilege[],
                ePrivileges.ADDEDITFAULTCATEGORY
              ) ? (
                <CustomDrawer
                  trigger={
                    <ActionCard text="Edit">
                      <EditFilled />
                    </ActionCard>
                  }
                  component={FaultCategoryForm}
                  componentProps={{
                    id: record.id,
                    onRefreshList: fetchData,
                  }}
                  title="Fault Category Form"
                  size="default"
                />
              ) : null}

              {Utils.isUserHasAccess(
                context.user?.privilegeList as TPrivilege[],
                ePrivileges.DELETEFAULTCATEGORY
              ) ? (
                <ActionCard text="Delete" onClick={() => handleDelete(record)}>
                  <DeleteOutlined />
                </ActionCard>
              ) : null}
            </div>
          }
          icon={
            <MoreOutlined
              style={{
                fontSize: "20px",
                cursor: "pointer",
                display:
                  Utils.isUserHasAccess(
                    context.user?.privilegeList as TPrivilege[],
                    ePrivileges.ADDEDITFAULTCATEGORY
                  ) ||
                  Utils.isUserHasAccess(
                    context.user?.privilegeList as TPrivilege[],
                    ePrivileges.DELETEFAULTCATEGORY
                  )
                    ? "visible"
                    : "none",
              }}
            />
          }
        />
      ),
    },
  ];
  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    (async () => {
      const savedPreferences: any[] = await loadColumnPreferences(
        FAULT_LIST_COLUMN_KEY
      );

      if (savedPreferences && savedPreferences.length > 0) {
        const savedPrefsMap: Map<string, any> = new Map(
          savedPreferences.map((pref: any) => [pref.key, pref])
        );
        const mergedColumns = initialColumns.map((col) => {
          const savedPref = savedPrefsMap.get(col.key) as any;
          return {
            ...col,
            hidden: savedPref?.hidden ?? false,
          };
        });
        initialColumns.forEach((col) => {
          if (!savedPrefsMap.has(col.key)) {
            if (!mergedColumns.find((c) => c.key === col.key)) {
              mergedColumns.push({ ...col, hidden: false });
            }
          }
        });

        setColumns(mergedColumns);
        setDraftColumns(mergedColumns);
      } else {
        setColumns(initialColumns);
        setDraftColumns(initialColumns);
      }
    })();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetFaultCategoryList, requestPayload);
      const { dataResponse, data, filterModel } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map(
          (item: TFaultCategory, index: number) => ({
            ...item,
            key: (item.id ?? "").toString(),
            srNo: index + 1,
          })
        );
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);
        updateColumnFilters(formattedData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Failed to fetch fault categroy data",
      });
      setIsLoading(false);
    }
  };

  const updateColumnFilters = (dataset: TFaultCategory[]) => {
    const getFilters = (key: keyof TFaultCategory) => {
      const uniqueValues = Array.from(
        new Set(
          dataset
            ?.map((item) => item?.[key])
            ?.filter(
              (val) =>
                val !== null && val !== undefined && val !== "" && val !== "-"
            ) as string[]
        )
      );

      return uniqueValues.map((value) => ({ text: value, value }));
    };
    const nameFilters = getFilters("problemName");

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.dataIndex === "problemName") {
          return { ...col, filters: nameFilters };
        }
        return col;
      })
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) => {
    const searchMatch = Object.values(item).some(
      (val) =>
        val && val.toString().toLowerCase().includes(searchText.toLowerCase())
    );

    const nameModelMatch =
      !activeFilters.problemName ||
      activeFilters.problemName.includes(item.problemName);

    return searchMatch && nameModelMatch;
  });

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns);
    clearColumnPreferences(FAULT_LIST_COLUMN_KEY);
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns);
    saveColumnPreferences(FAULT_LIST_COLUMN_KEY, draftColumns);
    setDrawerVisible(false);
  };

  const visibleColumns = columns?.filter((col) => !col?.hidden);

  const exportHeaders = visibleColumns
    ?.filter(
      (column) =>
        column?.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column?.title,
      key: column?.key,
    }));

  const allKeys = Array.from(
    new Set(data.flatMap((item) => Object.keys(item)))
  );

  const exportData = data.map((user) => {
    const mapped = {
      ...user,
    };
    const completeRow: Record<string, any> = {};
    allKeys.forEach((key) => {
      const val = (mapped as any)[key];
      completeRow[key] =
        val === null ||
        val === undefined ||
        val === "" ||
        (Array.isArray(val) && val.length === 0)
          ? "-"
          : val;
    });

    return completeRow;
  });

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
  };

  const deleteProduct = async (faultCategory: TFaultCategory) => {
    try {
      const payload = {
        data: {
          ...faultCategory,
          isDeleted: 1,
        },
      };
      const response = await post(AddEditFaultCategory, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleDeleteOk = async () => {
    if (faultCategory) {
      await deleteProduct(faultCategory);
    }
    setFaultCategory(null);
    setShowDeleteModal(false);
  };

  const handleDeleteCancel = () => {
    setFaultCategory(null);
    setShowDeleteModal(false);
  };

  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardFilled />}
        title={`${filterParams.totalRows} Total Fault Categories`}
        size="large"
      >
        <Space className="headerItems" wrap>
          {/* {Utils.isUserHasAccess(
              context.user?.privilegeList as TPrivilege[],
              ePrivileges.ADDCUSTOMERS
            ) ? ( */}
          <CustomDrawer
            trigger={
              <Button disabled={isLoading} type="primary">
                Add
              </Button>
            }
            component={FaultCategoryForm}
            componentProps={{
              id: 0,
              onRefreshList: fetchData,
            }}
            title="Fault Category Form"
            size="default"
          />
          {/* ) : null} */}
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`fault_categories_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined
              onClick={() => {
                setDraftColumns(columns);
                setDrawerVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          // sticky
          scroll={{
            x: "max-content",
          }}
        />
      </div>

      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>

      <CustomModal
        title="Confirm Delete"
        isOpen={showDeleteModal}
        onOk={handleDeleteOk}
        onCancel={() => handleDeleteCancel()}
        okText="Yes"
        cancelText="No"
        cusHeader={true}
      >
        <p>
          Are you about to delete <b>{faultCategory?.problemName}</b>?
        </p>
      </CustomModal>
    </div>
  );
}
